import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Copy, Check, Share2, Twitter, MessageCircle, Send, Gift, Users, Trophy, X, Smartphone, Instagram, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import Confetti from "./Confetti";

const ease = [0.2, 0.8, 0.2, 1] as [number, number, number, number];

interface ShareScreenProps {
  referralCode: string;
  onClose: () => void;
  onContinue?: () => Promise<void> | void;
  continueLabel?: string;
}

export function ShareScreen({ referralCode, onClose, onContinue, continueLabel = "Go to Dashboard" }: ShareScreenProps) {
  const [copied, setCopied] = useState(false);
  const [referralCount, setReferralCount] = useState(0);
  const [showConfetti, setShowConfetti] = useState(true);
  const [continuing, setContinuing] = useState(false);

  const shareUrl = `${window.location.origin}?ref=${referralCode}`;
  const shareText = `I just created my FREE Summer 2026 bracket on FC Bracket 🏆⚽ Build yours and compete with me! 🎉`;

  useEffect(() => {
    // Poll referral count via SECURITY DEFINER RPC. The table is no longer
    // publicly readable (RLS), so realtime subscriptions don't deliver.
    let cancelled = false;
    let lastCount = -1;

    const fetchCount = async () => {
      const { data } = await supabase
        .rpc("lookup_referral_count", { p_code: referralCode })
        .single();
      if (cancelled || data === null || data === undefined) return;
      const next = data as unknown as number;
      if (lastCount !== -1 && next > lastCount) {
        toast.success("🔥 Someone just signed up from your link!");
      }
      lastCount = next;
      setReferralCount(next);
    };

    fetchCount();
    const interval = setInterval(fetchCount, 15000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [referralCode]);

  const handleContinue = async () => {
    if (continuing) return;

    if (!onContinue) {
      onClose();
      return;
    }

    setContinuing(true);
    try {
      await onContinue();
    } finally {
      setContinuing(false);
    }
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Link copied! Now share it everywhere 🚀");
    setTimeout(() => setCopied(false), 2000);
  };

  const shareTwitter = () => {
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
      "_blank"
    );
  };

  const shareWhatsApp = () => {
    window.open(
      `https://wa.me/?text=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`,
      "_blank"
    );
  };

  const shareSMS = () => {
    const body = `${shareText}\n\n${shareUrl}`;
    window.open(`sms:?&body=${encodeURIComponent(body)}`, "_self");
  };

  const shareInstagram = () => {
    // Copy link first since Instagram doesn't have a direct share URL
    navigator.clipboard.writeText(shareUrl).then(() => {
      toast.success("Link copied! Paste it in your Instagram story or DM 📸");
      window.open("https://www.instagram.com/", "_blank");
    });
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "FC Bracket — Summer 2026", text: shareText, url: shareUrl });
      } catch {
        // User cancelled share sheet or share failed — no-op is safe here.
      }
    } else {
      copyLink();
    }
  };

  const milestones = [
    { count: 1, label: "1 friend", reward: "+1 Free Bracket", icon: Gift },
    { count: 3, label: "3 friends", reward: "Early Access", icon: Trophy },
    { count: 5, label: "5 friends", reward: "VIP Status", icon: Users },
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-xl overflow-y-auto"
      >
        {showConfetti && <Confetti onComplete={() => setShowConfetti(false)} />}

        {/* Top bar */}
        <div className="fixed top-4 left-4 right-4 z-[110] flex justify-between items-center">
          <Button
            onClick={handleContinue}
            variant="hero"
            size="sm"
            className="font-body font-semibold"
            disabled={continuing}
          >
            {continuing ? "Sending magic link..." : continueLabel} <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
          <button
            onClick={handleContinue}
            disabled={continuing}
            className="p-2 rounded-full bg-secondary/80 hover:bg-secondary text-muted-foreground hover:text-foreground snap-transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="min-h-screen flex items-center justify-center p-4 sm:p-6">
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ type: "tween", ease, duration: 0.6, delay: 0.2 }}
            className="w-full max-w-lg"
          >
            {/* Header */}
            <div className="text-center mb-8">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.4 }}
                className="text-6xl sm:text-7xl mb-4"
              >
                🎉
              </motion.div>
              <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="font-display text-4xl sm:text-5xl md:text-6xl mb-2"
              >
                YOU'RE <span className="text-primary">IN!</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="font-body text-muted-foreground text-base sm:text-lg"
              >
                Now unlock <strong className="text-foreground">bonus rewards</strong> by sharing with friends
              </motion.p>
            </div>

            {/* Referral counter */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="surface-card-gold p-5 sm:p-6 text-center mb-6"
            >
              <p className="font-body text-xs uppercase tracking-widest text-primary mb-2">Your Referrals</p>
              <p className="font-display text-5xl sm:text-6xl text-foreground tabular-nums">{referralCount}</p>
              <p className="font-body text-sm text-muted-foreground mt-1">
                {referralCount === 0 ? "Share your link to start earning" : `${referralCount} friend${referralCount !== 1 ? "s" : ""} joined!`}
              </p>
            </motion.div>

            {/* Milestones */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="surface-card p-5 sm:p-6 mb-6"
            >
              <p className="font-display text-lg sm:text-xl text-foreground mb-4">🎁 UNLOCK REWARDS</p>
              <div className="space-y-3">
                {milestones.map((m) => {
                  const unlocked = referralCount >= m.count;
                  return (
                    <div
                      key={m.count}
                      className={`flex items-center gap-3 sm:gap-4 p-3 rounded-lg snap-transition ${
                        unlocked
                          ? "bg-primary/10 border border-primary/30"
                          : "bg-secondary/50 border border-border"
                      }`}
                    >
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                          unlocked ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                        }`}
                      >
                        <m.icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-body text-sm font-semibold ${unlocked ? "text-primary" : "text-foreground"}`}>
                          {m.reward}
                        </p>
                        <p className="font-body text-xs text-muted-foreground">
                          Invite {m.label}
                        </p>
                      </div>
                      {unlocked && (
                        <motion.span
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="text-primary font-display text-sm"
                        >
                          UNLOCKED ✓
                        </motion.span>
                      )}
                    </div>
                  );
                })}
              </div>
            </motion.div>

            {/* Share link */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="mb-6"
            >
              <div
                onClick={copyLink}
                className="flex items-center gap-2 surface-card-inner p-3 sm:p-4 cursor-pointer hover:shadow-[0_0_0_1.5px_hsla(40,95%,55%,0.4)] snap-transition"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-body text-xs text-muted-foreground mb-1">Your unique link</p>
                  <p className="font-body text-sm text-foreground truncate">{shareUrl}</p>
                </div>
                <Button variant="hero" size="sm" className="shrink-0">
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  <span className="ml-1.5 hidden sm:inline">{copied ? "Copied!" : "Copy"}</span>
                </Button>
              </div>
            </motion.div>

            {/* Social share buttons */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0 }}
              className="grid grid-cols-2 gap-3 mb-6"
            >
              <Button
                onClick={shareSMS}
                className="bg-[#34C759] hover:bg-[#2db84d] text-white font-body font-semibold h-12 sm:h-14 text-sm"
              >
                <Smartphone className="w-4 h-4 mr-2" /> Text Message
              </Button>
              <Button
                onClick={shareWhatsApp}
                className="bg-[#25D366] hover:bg-[#1fb855] text-white font-body font-semibold h-12 sm:h-14 text-sm"
              >
                <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
              </Button>
              <Button
                onClick={shareTwitter}
                className="bg-[#1DA1F2] hover:bg-[#1a8cd8] text-white font-body font-semibold h-12 sm:h-14 text-sm"
              >
                <Twitter className="w-4 h-4 mr-2" /> Share on X
              </Button>
              <Button
                onClick={shareInstagram}
                className="bg-gradient-to-r from-[#833AB4] via-[#E1306C] to-[#F77737] hover:opacity-90 text-white font-body font-semibold h-12 sm:h-14 text-sm"
              >
                <Instagram className="w-4 h-4 mr-2" /> Instagram
              </Button>
            </motion.div>

            {/* Bottom text */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1 }}
              className="text-center font-body text-xs text-muted-foreground"
            >
              Rewards are applied automatically to your account 🚀
            </motion.p>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
